export {};

type AppConfig = {
  REACT_APP_API_BASE_URL?: string;
  REACT_APP_WEB_BASE_URL?: string;
  REACT_APP_BLOB_PUBLIC_URL?: string;
  REACT_APP_AZURE_CLIENT_ID?: string;
  REACT_APP_REDIRECT_URI?: string;
  REACT_APP_POST_LOGOUT_REDIRECT_URI?: string;
  REACT_APP_AUTH_SCOPE?: string;
  REACT_APP_CONVERSION_RESULT_STORAGE_KEY?: string;
  REACT_APP_ANALYSIS_RESULT_STORAGE_KEY?: string;
  REACT_APP_COMPARISON_RESULT_STORAGE_KEY?: string;
  REACT_APP_COMPANY_STANDARD_DOCUMENTS_STORAGE_KEY?: string;
  REACT_APP_CONTRADICTION_RESULT_STORAGE_KEY?: string;
};

declare global {
  interface Window {
    __APP_CONFIG__?: AppConfig;
  }
}

const buildConfig: AppConfig = {
  REACT_APP_API_BASE_URL: process.env.REACT_APP_API_BASE_URL,
  REACT_APP_WEB_BASE_URL: process.env.REACT_APP_WEB_BASE_URL,
  REACT_APP_BLOB_PUBLIC_URL: process.env.REACT_APP_BLOB_PUBLIC_URL,
  REACT_APP_AZURE_CLIENT_ID: process.env.REACT_APP_AZURE_CLIENT_ID,
  REACT_APP_REDIRECT_URI: process.env.REACT_APP_REDIRECT_URI,
  REACT_APP_POST_LOGOUT_REDIRECT_URI: process.env.REACT_APP_POST_LOGOUT_REDIRECT_URI,
  REACT_APP_AUTH_SCOPE: process.env.REACT_APP_AUTH_SCOPE,
  REACT_APP_CONVERSION_RESULT_STORAGE_KEY:
    process.env.REACT_APP_CONVERSION_RESULT_STORAGE_KEY,
  REACT_APP_ANALYSIS_RESULT_STORAGE_KEY:
    process.env.REACT_APP_ANALYSIS_RESULT_STORAGE_KEY,
  REACT_APP_COMPARISON_RESULT_STORAGE_KEY:
    process.env.REACT_APP_COMPARISON_RESULT_STORAGE_KEY,
  REACT_APP_COMPANY_STANDARD_DOCUMENTS_STORAGE_KEY:
    process.env.REACT_APP_COMPANY_STANDARD_DOCUMENTS_STORAGE_KEY,
  REACT_APP_CONTRADICTION_RESULT_STORAGE_KEY:
    process.env.REACT_APP_CONTRADICTION_RESULT_STORAGE_KEY,
};

const getConfigValue = (
  key: keyof AppConfig,
  fallback: string = "",
): string => {
  const runtimeValue = window.__APP_CONFIG__?.[key];
  if (
    runtimeValue !== undefined &&
    runtimeValue !== null &&
    runtimeValue !== ""
  ) {
    return runtimeValue;
  }

  const buildValue = buildConfig[key];
  if (buildValue !== undefined && buildValue !== null && buildValue !== "") {
    return buildValue;
  }

  return fallback;
};

export const config = {
  // The localhost default is for local dev only. In a built app a missing setting must not
  // silently point every request (including /api/blob-proxy image URLs) at localhost.
  baseURL: getConfigValue(
    "REACT_APP_API_BASE_URL",
    import.meta.env.DEV ? "http://localhost:7143/api" : "",
  ),
  WEB_BASE_URL: getConfigValue("REACT_APP_WEB_BASE_URL"),
  BLOB_PUBLIC_URL: getConfigValue("REACT_APP_BLOB_PUBLIC_URL"),
  AZURE_CLIENT_ID: getConfigValue("REACT_APP_AZURE_CLIENT_ID"),
  AUTH_SCOPE: getConfigValue("REACT_APP_AUTH_SCOPE", "openid profile email"),
  AUTHORITY: "https://login.microsoftonline.com/common",
  AUTH_REDIRECT_URI: getConfigValue("REACT_APP_REDIRECT_URI", "/"),
  AUTH_LOGOUT_REDIRECT_URI: getConfigValue(
    "REACT_APP_POST_LOGOUT_REDIRECT_URI",
    "/",
  ),
  CONVERSION_RESULT_STORAGE_KEY: getConfigValue(
    "REACT_APP_CONVERSION_RESULT_STORAGE_KEY",
    "conversionResult",
  ),
  ANALYSIS_RESULT_STORAGE_KEY: getConfigValue(
    "REACT_APP_ANALYSIS_RESULT_STORAGE_KEY",
    "documentAnalysisResult",
  ),
  COMPARISON_RESULT_STORAGE_KEY: getConfigValue(
    "REACT_APP_COMPARISON_RESULT_STORAGE_KEY",
    "specsComparisonResult",
  ),
  COMPANY_STANDARD_DOCUMENTS_STORAGE_KEY: getConfigValue(
    "REACT_APP_COMPANY_STANDARD_DOCUMENTS_STORAGE_KEY",
    "companyStandardDocuments",
  ),
  CONTRADICTION_RESULT_STORAGE_KEY: getConfigValue(
    "REACT_APP_CONTRADICTION_RESULT_STORAGE_KEY",
    "contradictionResult",
  ),
};

// Names the cause immediately instead of leaving a page full of broken images and failed
// requests: config.js was not generated from App Service settings at startup.
if (!import.meta.env.DEV && !config.baseURL) {
  console.error(
    "REACT_APP_API_BASE_URL is not set — check config.js / App Service application settings",
  );
}
